// ABOUTME: Dialog to import a configuration bundle for one entity — validates schema compatibility, then imports.
// ABOUTME: Reads a JSON file, checks it against the environment, and shows a per-item import result.
import {Check, Close, WarningAmber} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import React, {ChangeEvent, useState} from 'react'
import {useSnackbar} from 'notistack'

import {useTranslation} from '../../../../hooks/useTranslation'
import {
  ImportCompatibilityReport,
  ImportReport,
  useImportConfig,
  useValidateImport
} from '../hooks/useConfigImportExport'
import {EntityType} from '../types'

interface ConfigImportDialogProps {
  open: boolean
  onClose: () => void
  entityType: EntityType
  entityName: string
  countryId: string
  businessLine: string
}

const CompatibilityAlert: React.FC<{compat: ImportCompatibilityReport}> = ({compat}) => {
  const {t} = useTranslation()

  if (compat.compatible) {
    return (
      <Alert severity="success" icon={<Check fontSize="inherit" />} sx={{mt: 2}}>
        {t('countryConfigurations.importExport.importDialog.compatible')}
      </Alert>
    )
  }

  return (
    <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />} sx={{mt: 2}}>
      {t('countryConfigurations.importExport.importDialog.incompatible')}
      <List dense disablePadding>
        {compat.issues.map((issue) => (
          <ListItem key={issue} disablePadding>
            <ListItemText primary={issue} />
          </ListItem>
        ))}
      </List>
    </Alert>
  )
}

const ImportResultView: React.FC<{report: ImportReport}> = ({report}) => {
  const {t} = useTranslation()
  const failed = report.results.filter((r) => r.status === 'Failed')

  return (
    <>
      <Alert severity={report.failed === 0 ? 'success' : 'warning'}>
        {t('countryConfigurations.importExport.importDialog.resultSummary', {
          created: report.created,
          updated: report.updated,
          failed: report.failed
        })}
      </Alert>
      {failed.length > 0 && (
        <List dense>
          {failed.map((r) => (
            <ListItem key={r.scope} disablePadding>
              <ListItemText primary={r.scope} secondary={r.reason ?? ''} />
            </ListItem>
          ))}
        </List>
      )}
    </>
  )
}

const ImportPickView: React.FC<{
  fileName: string | null
  parseError: string | null
  validating: boolean
  compat: ImportCompatibilityReport | null
  busy: boolean
  onFile: (event: ChangeEvent<HTMLInputElement>) => void
}> = ({fileName, parseError, validating, compat, busy, onFile}) => {
  const {t} = useTranslation()

  return (
    <>
      <Button component="label" variant="outlined" disabled={busy}>
        {t('countryConfigurations.importExport.importDialog.chooseFile')}
        <input type="file" accept=".json,application/json" hidden onChange={onFile} />
      </Button>
      {fileName && (
        <Typography variant="body2" sx={{mt: 1}}>
          {fileName}
        </Typography>
      )}
      {parseError && (
        <Alert severity="error" sx={{mt: 2}}>
          {parseError}
        </Alert>
      )}
      {validating && (
        <Typography variant="body2" sx={{mt: 2}}>
          {t('countryConfigurations.importExport.importDialog.validating')}
        </Typography>
      )}
      {compat && <CompatibilityAlert compat={compat} />}
    </>
  )
}

export const ConfigImportDialog: React.FC<ConfigImportDialogProps> = ({
  open,
  onClose,
  entityType,
  entityName,
  countryId,
  businessLine
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()
  const validate = useValidateImport()
  const importConfig = useImportConfig(countryId, businessLine)

  const [fileName, setFileName] = useState<string | null>(null)
  const [bundle, setBundle] = useState<unknown>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [compat, setCompat] = useState<ImportCompatibilityReport | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)

  const reset = () => {
    setFileName(null)
    setBundle(null)
    setParseError(null)
    setCompat(null)
    setReport(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    reset()
    const file = event.target.files?.[0]
    // Clear the input so re-selecting the same file re-triggers onChange.
    event.target.value = ''
    if (!file) return

    setFileName(file.name)
    try {
      const parsed = JSON.parse(await file.text())
      setBundle(parsed)
      setCompat(await validate.mutateAsync({entityType, bundle: parsed}))
    } catch {
      setParseError(t('countryConfigurations.importExport.importDialog.invalidFile'))
    }
  }

  const runImport = async (force: boolean) => {
    if (bundle === null) return
    try {
      const result = await importConfig.mutateAsync({entityType, bundle, force})
      setReport(result)
      if (result.failed === 0) {
        enqueueSnackbar(
          t('countryConfigurations.importExport.importDialog.success', {
            created: result.created,
            updated: result.updated
          }),
          {variant: 'success', anchorOrigin: {vertical: 'top', horizontal: 'center'}}
        )
      }
    } catch {
      enqueueSnackbar(t('countryConfigurations.importExport.importDialog.error'), {
        variant: 'error',
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      })
    }
  }

  const busy = validate.isPending || importConfig.isPending
  const canImport = compat?.compatible === true
  const canForce = compat !== null && !compat.compatible

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <DialogTitle>
          {t('countryConfigurations.importExport.importDialog.title', {entityName})}
        </DialogTitle>
        <IconButton
          onClick={handleClose}
          sx={{mr: 1}}
          aria-label={t('countryConfigurations.importExport.importDialog.close')}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent dividers>
        {report ? (
          <ImportResultView report={report} />
        ) : (
          <ImportPickView
            fileName={fileName}
            parseError={parseError}
            validating={validate.isPending}
            compat={compat}
            busy={busy}
            onFile={handleFile}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />}>
          {report
            ? t('countryConfigurations.importExport.importDialog.close')
            : t('countryConfigurations.importExport.importDialog.cancel')}
        </Button>
        {!report && canImport && (
          <Button variant="contained" startIcon={<Check />} disabled={busy} onClick={() => runImport(false)}>
            {t('countryConfigurations.importExport.importDialog.import')}
          </Button>
        )}
        {!report && canForce && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<WarningAmber />}
            disabled={busy}
            onClick={() => runImport(true)}
          >
            {t('countryConfigurations.importExport.importDialog.importAnyway')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
